import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  format,
  addDays,
  startOfDay,
  isSameDay,
} from 'date-fns';
import clsx from 'clsx';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  MapPin,
  Check,
  RotateCcw,
} from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { getServiceCallListCustomerName } from '@shared/utils';
import { useDispatchWorkspace } from './DispatchContext';
import {
  buildTechColorMap,
  technicianName,
  VISIT_BUCKET_META,
  formatLocationAge,
} from './constants';

const toDateParam = (date) => format(startOfDay(date), 'yyyy-MM-dd');

function VisitRow({ visit, onClick }) {
  const meta = VISIT_BUCKET_META[visit.bucket] || VISIT_BUCKET_META.remaining;
  const customerName = getServiceCallListCustomerName(visit);

  return (
    <button
      type="button"
      onClick={() => onClick(visit)}
      className={clsx(
        'w-full text-left rounded-md px-2 py-1.5 transition-colors hover:bg-surface-hover',
        meta.rowClass
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 pt-0.5 flex flex-col items-center gap-0.5">
          <span className="text-2xs font-semibold tabular-nums text-gray-400 w-4 text-center">
            {visit.routeOrder < 9999 ? visit.routeOrder : '—'}
          </span>
          <span
            className={clsx('h-2.5 w-2.5 rounded-full flex-shrink-0', meta.dotClass)}
            aria-hidden
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            {visit.bucket === 'done' && (
              <Check className="h-3 w-3 text-emerald-600 flex-shrink-0" aria-hidden />
            )}
            <span
              className={clsx(
                'text-xs font-medium truncate',
                visit.bucket === 'done' && 'line-through text-gray-500'
              )}
            >
              {customerName}
            </span>
          </div>
          {visit.addressLine && (
            <p className="text-2xs text-gray-500 truncate mt-0.5">{visit.addressLine}</p>
          )}
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            {visit.type?.name && (
              <span className="text-2xs px-1 py-0 rounded bg-surface-secondary text-gray-600">
                {visit.type.name}
              </span>
            )}
            {visit.status === 'return' && (
              <span className="text-2xs px-1 py-0 rounded bg-amber-100 text-amber-800 inline-flex items-center gap-0.5">
                <RotateCcw className="h-2.5 w-2.5" />
                Needs return
              </span>
            )}
            {visit.isPriority && (
              <span className="text-2xs px-1 py-0 rounded bg-orange-100 text-orange-800">
                Priority
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function TechColumn({ tech, color, onRefreshTech, isRefreshing, onVisitClick }) {
  const progressPct = tech.totalCount > 0
    ? Math.round((tech.doneCount / tech.totalCount) * 100)
    : 0;
  const locationAge = formatLocationAge(tech.location?.recordedAt);

  return (
    <div
      className="flex-shrink-0 w-56 flex flex-col rounded-lg border border-surface-border bg-surface h-full"
      style={{ borderTopWidth: 3, borderTopColor: color }}
    >
      <div className="flex-shrink-0 px-2.5 py-2 border-b border-surface-border space-y-1.5">
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs font-semibold truncate">{technicianName(tech)}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onRefreshTech(tech.id)}
              disabled={isRefreshing}
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Refresh route order"
            >
              <RefreshCw className={clsx('h-3 w-3', isRefreshing && 'animate-spin')} />
            </button>
            <span className="text-2xs tabular-nums font-semibold text-gray-600">
              {tech.doneCount}/{tech.totalCount}
            </span>
          </div>
        </div>

        <div className="h-1 rounded-full bg-surface-secondary overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex items-center gap-1 text-2xs">
          <MapPin className={clsx('h-3 w-3 flex-shrink-0', locationAge ? 'text-primary-500' : 'text-gray-300')} />
          {locationAge ? (
            <span className="text-gray-600">Last seen {locationAge}</span>
          ) : (
            <span className="text-gray-400">No location yet</span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-1.5 space-y-0.5 min-h-0">
        {tech.visits.length === 0 ? (
          <p className="text-2xs text-gray-400 text-center py-4 px-2">
            No visits scheduled today
          </p>
        ) : (
          tech.visits.map((visit) => (
            <VisitRow key={visit.id} visit={visit} onClick={onVisitClick} />
          ))
        )}
      </div>
    </div>
  );
}

export default function TodayView() {
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [refreshingTechId, setRefreshingTechId] = useState(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const queryClient = useQueryClient();
  const { setSelectedDetail } = useDispatchWorkspace();

  const dateParam = toDateParam(selectedDate);
  const isToday = isSameDay(selectedDate, startOfDay(new Date()));

  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery(
    ['todayOverview', dateParam],
    () => serviceCallsAPI.getTodayOverview({ date: dateParam }).then((res) => res.data),
    { refetchInterval: 20000, keepPreviousData: true }
  );

  const technicians = data?.technicians || [];
  const techColorMap = useMemo(
    () => buildTechColorMap(technicians),
    [technicians]
  );

  const invalidateOverview = useCallback(() => {
    queryClient.invalidateQueries(['todayOverview', dateParam]);
  }, [queryClient, dateParam]);

  const refreshRoute = useCallback(async ({ technicianId } = {}) => {
    if (technicianId) {
      setRefreshingTechId(technicianId);
    } else {
      setIsRefreshingAll(true);
    }
    try {
      await serviceCallsAPI.getTodayOverview({
        date: dateParam,
        refresh: true,
        technicianId: technicianId || undefined,
      });
      invalidateOverview();
    } finally {
      setRefreshingTechId(null);
      setIsRefreshingAll(false);
    }
  }, [dateParam, invalidateOverview]);

  const handleVisitClick = (visit) => {
    setSelectedDetail({ type: 'serviceCall', serviceCall: visit });
  };

  const lastUpdated = dataUpdatedAt
    ? format(new Date(dataUpdatedAt), 'h:mm:ss a')
    : null;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-shrink-0 px-3 py-2 border-b border-surface-border bg-surface flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSelectedDate((d) => addDays(d, -1))}
            className="btn-sm btn-ghost p-1"
            title="Previous day"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs font-semibold min-w-[7rem] text-center">
            {format(selectedDate, isToday ? "'Today,' MMM d" : 'EEE, MMM d, yyyy')}
          </span>
          <button
            type="button"
            onClick={() => setSelectedDate((d) => addDays(d, 1))}
            className="btn-sm btn-ghost p-1"
            title="Next day"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {!isToday && (
            <button
              type="button"
              onClick={() => setSelectedDate(startOfDay(new Date()))}
              className="btn-sm btn-secondary ml-1"
            >
              Today
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => refreshRoute()}
          disabled={isRefreshingAll}
          className="btn-sm btn-secondary inline-flex items-center gap-1"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', isRefreshingAll && 'animate-spin')} />
          Refresh route
        </button>

        {isLoading && <span className="text-2xs text-gray-400">Loading...</span>}
        {!isLoading && lastUpdated && (
          <span className="text-2xs text-gray-400 ml-auto">
            Updated {lastUpdated}
            {isFetching && !isLoading && ' · refreshing…'}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
        <div className="flex gap-3 p-3 h-full min-w-max">
          {technicians.length === 0 && !isLoading ? (
            <div className="flex items-center justify-center w-full text-sm text-gray-500">
              No active technicians
            </div>
          ) : (
            technicians.map((tech) => (
              <TechColumn
                key={tech.id}
                tech={tech}
                color={techColorMap[tech.id]}
                onRefreshTech={(id) => refreshRoute({ technicianId: id })}
                isRefreshing={refreshingTechId === tech.id || isRefreshingAll}
                onVisitClick={handleVisitClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
